import { supabase } from './supabase';
import { unifiedStorageManager } from './unifiedStorageManager';

/**
 * Check if a resource is active for the given auth user ID
 */
export async function checkResourceActive(authUserId: string) {
  if (!supabase) {
    return {
      isActive: false,
      error: 'Supabase client not initialized'
    };
  }

  try {
    const { data, error } = await supabase
      .from('resources')
      .select('id, status')
      .eq('auth_user_id', authUserId)
      .single();

    if (error) throw error;

    return {
      isActive: data?.status === 'active',
      error: null
    };
  } catch (err) {
    return {
      isActive: false,
      error: err instanceof Error ? err.message : 'Unknown error checking resource status'
    };
  }
}

/**
 * Comprehensive sign out function that clears all cached data and storage
 */
export async function signOut() {

  if (!supabase) return { error: 'Supabase client not initialized' };
  
  try {
    console.log('🚪 Starting comprehensive sign out process...');
    
    // 1. FIRST: Sign out from Supabase (this triggers auth state change events)
    console.log('🔍 About to call supabase.auth.signOut()...');
    console.log('🔍 Supabase client status:', supabase ? 'Available' : 'Not available');
    console.log('🔍 Supabase auth status:', supabase?.auth ? 'Available' : 'Not available');
    
    try {
      const { error } = await supabase.auth.signOut();
      if (error) {
        console.warn('Supabase sign out error:', error);
      } else {
        console.log('✅ Supabase sign out successful');
      }
    } catch (signOutError) {
      console.error('❌ Exception during supabase.auth.signOut():', signOutError);
      // Continue with cleanup even if Supabase sign out fails
    }
    
    // 2. Wait a moment for auth state change events to propagate
    await new Promise(resolve => setTimeout(resolve, 100));
    
    // 3. Clear all auth-related localStorage items
    const authKeysToRemove = [
      'cachedUserSession',
      'enhancedLoginInProgress',
      'skipInitializing',
      'preloadedRoute',
      'usePreloadedBusinessData',
      'selectedBusiness',
      'businesses',
      'nexus-auth-backup',
      'nexus-auth-backup-timestamp',
      'nexus-booking-auth',
      'nexus_impersonation_data'
    ];
    
    // Remove specific auth keys
    authKeysToRemove.forEach(key => {
      try {
        localStorage.removeItem(key);
        console.log(`🧹 Removed localStorage key: ${key}`);
      } catch (e) {
        console.warn(`Failed to remove localStorage key ${key}:`, e);
      }
    });
    
    // 4. Clear all Supabase-related localStorage items
    Object.keys(localStorage).forEach(key => {
      if (key.startsWith('sb-') || key.startsWith('supabase.auth.') || key.includes('auth')) {
        try {
          localStorage.removeItem(key);
          console.log(`🧹 Removed Supabase localStorage key: ${key}`);
        } catch (e) {
          console.warn(`Failed to remove Supabase localStorage key ${key}:`, e);
        }
      }
    });
    
    // 5. Clear sessionStorage
    try {
      sessionStorage.clear();
      console.log('🧹 Cleared sessionStorage');
    } catch (e) {
      console.warn('Failed to clear sessionStorage:', e);
    }
    
    // 6. Clear unified storage (IndexedDB)
    try {
      await unifiedStorageManager.removeItem('nexus-auth-backup');
      await unifiedStorageManager.removeItem('nexus-auth-backup-timestamp');
      await unifiedStorageManager.removeItem('nexus-booking-auth');
      console.log('🧹 Cleared unified storage');
    } catch (e) {
      console.warn('Failed to clear unified storage:', e);
    }
    
    // 7. Clear IndexedDB databases
    if ('indexedDB' in window) {
      try {
        // Clear supabase-auth database
        const supabaseRequest = indexedDB.deleteDatabase('supabase-auth');
        supabaseRequest.onsuccess = () => console.log('🧹 Cleared supabase-auth IndexedDB');
        supabaseRequest.onerror = () => console.warn('Failed to clear supabase-auth IndexedDB');
        
        // Clear nexus-auth-storage database
        const nexusRequest = indexedDB.deleteDatabase('nexus-auth-storage');
        nexusRequest.onsuccess = () => console.log('🧹 Cleared nexus-auth-storage IndexedDB');
        nexusRequest.onerror = () => console.warn('Failed to clear nexus-auth-storage IndexedDB');
        
        // Clear nexus-unified-storage database
        const unifiedRequest = indexedDB.deleteDatabase('nexus-unified-storage');
        unifiedRequest.onsuccess = () => console.log('🧹 Cleared nexus-unified-storage IndexedDB');
        unifiedRequest.onerror = () => console.warn('Failed to clear nexus-unified-storage IndexedDB');
      } catch (e) {
        console.warn('Failed to clear IndexedDB databases:', e);
      }
    }
    
    // 8. Clear service worker cache (if available)
    if ('caches' in window) {
      try {
        const cacheNames = await caches.keys();
        await Promise.all(
          cacheNames.map(cacheName => {
            console.log(`🧹 Clearing cache: ${cacheName}`);
            return caches.delete(cacheName);
          })
        );
        console.log('🧹 Cleared all service worker caches');
      } catch (e) {
        console.warn('Failed to clear service worker caches:', e);
      }
    }
    
    // 9. Unregister service workers
    if ('serviceWorker' in navigator) {
      try {
        const registrations = await navigator.serviceWorker.getRegistrations();
        await Promise.all(
          registrations.map(registration => {
            console.log('🧹 Unregistering service worker');
            return registration.unregister();
          })
        );
        console.log('🧹 Unregistered all service workers');
      } catch (e) {
        console.warn('Failed to unregister service workers:', e);
      }
    }
    
    // 10. Clear any remaining auth state
    try {
      // Clear any remaining auth-related data
      const remainingAuthKeys = Object.keys(localStorage).filter(key => 
        key.includes('auth') || key.includes('session') || key.includes('token') || key.includes('user')
      );
      
      remainingAuthKeys.forEach(key => {
        try {
          localStorage.removeItem(key);
          console.log(`🧹 Removed remaining auth key: ${key}`);
        } catch (e) {
          console.warn(`Failed to remove remaining auth key ${key}:`, e);
        }
      });
    } catch (e) {
      console.warn('Failed to clear remaining auth data:', e);
    }
    
    console.log('✅ Comprehensive sign out completed successfully');
    return { error: null };
    
  } catch (err) {
    console.error('❌ Error during comprehensive sign out:', err);
    return { error: err instanceof Error ? err.message : 'Unknown error during sign out' };
  }
} 

/**
 * Utility function to clear all cached data and storage without signing out
 * Useful for debugging or manual cleanup
 */
export async function clearAllStorage(): Promise<void> {
  try {
    console.log('🧹 Starting comprehensive storage cleanup...');
    
    // 1. Clear all auth-related localStorage items
    const authKeysToRemove = [
      'cachedUserSession',
      'enhancedLoginInProgress',
      'skipInitializing',
      'preloadedRoute',
      'usePreloadedBusinessData',
      'selectedBusiness',
      'businesses',
      'nexus-auth-backup',
      'nexus-auth-backup-timestamp',
      'nexus-booking-auth',
      'nexus_impersonation_data'
    ];
    
    // Remove specific auth keys
    authKeysToRemove.forEach(key => {
      try {
        localStorage.removeItem(key);
        console.log(`🧹 Removed localStorage key: ${key}`);
      } catch (e) {
        console.warn(`Failed to remove localStorage key ${key}:`, e);
      }
    });
    
    // 2. Clear all Supabase-related localStorage items
    Object.keys(localStorage).forEach(key => {
      if (key.startsWith('sb-') || key.startsWith('supabase.auth.') || key.includes('auth')) {
        try {
          localStorage.removeItem(key);
          console.log(`🧹 Removed Supabase localStorage key: ${key}`);
        } catch (e) {
          console.warn(`Failed to remove Supabase localStorage key ${key}:`, e);
        }
      }
    });
    
    // 3. Clear sessionStorage
    try {
      sessionStorage.clear();
      console.log('🧹 Cleared sessionStorage');
    } catch (e) {
      console.warn('Failed to clear sessionStorage:', e);
    }
    
    // 4. Clear unified storage (IndexedDB)
    try {
      await unifiedStorageManager.removeItem('nexus-auth-backup');
      await unifiedStorageManager.removeItem('nexus-auth-backup-timestamp');
      await unifiedStorageManager.removeItem('nexus-booking-auth');
      console.log('🧹 Cleared unified storage');
    } catch (e) {
      console.warn('Failed to clear unified storage:', e);
    }
    
    // 5. Clear IndexedDB databases
    if ('indexedDB' in window) {
      try {
        // Clear supabase-auth database
        const supabaseRequest = indexedDB.deleteDatabase('supabase-auth');
        supabaseRequest.onsuccess = () => console.log('🧹 Cleared supabase-auth IndexedDB');
        supabaseRequest.onerror = () => console.warn('Failed to clear supabase-auth IndexedDB');
        
        // Clear nexus-auth-storage database
        const nexusRequest = indexedDB.deleteDatabase('nexus-auth-storage');
        nexusRequest.onsuccess = () => console.log('🧹 Cleared nexus-auth-storage IndexedDB');
        nexusRequest.onerror = () => console.warn('Failed to clear nexus-auth-storage IndexedDB');
        
        // Clear nexus-unified-storage database
        const unifiedRequest = indexedDB.deleteDatabase('nexus-unified-storage');
        unifiedRequest.onsuccess = () => console.log('🧹 Cleared nexus-unified-storage IndexedDB');
        unifiedRequest.onerror = () => console.warn('Failed to clear nexus-unified-storage IndexedDB');
      } catch (e) {
        console.warn('Failed to clear IndexedDB databases:', e);
      }
    }
    
    // 6. Clear service worker cache (if available)
    if ('caches' in window) {
      try {
        const cacheNames = await caches.keys();
        await Promise.all(
          cacheNames.map(cacheName => {
            console.log(`🧹 Clearing cache: ${cacheName}`);
            return caches.delete(cacheName);
          })
        );
        console.log('🧹 Cleared all service worker caches');
      } catch (e) {
        console.warn('Failed to clear service worker caches:', e);
      }
    }
    
    // 7. Clear any remaining auth state
    try {
      // Clear any remaining auth-related data
      const remainingAuthKeys = Object.keys(localStorage).filter(key => 
        key.includes('auth') || key.includes('session') || key.includes('token') || key.includes('user')
      );
      
      remainingAuthKeys.forEach(key => {
        try {
          localStorage.removeItem(key);
          console.log(`🧹 Removed remaining auth key: ${key}`);
        } catch (e) {
          console.warn(`Failed to remove remaining auth key ${key}:`, e);
        }
      });
    } catch (e) {
      console.warn('Failed to clear remaining auth data:', e);
    }
    
    console.log('✅ Comprehensive storage cleanup completed successfully');
  } catch (err) {
    console.error('❌ Error during storage cleanup:', err);
    throw err;
  }
} 